import * as TooltipPrimitive from "@radix-ui/react-tooltip";

/**
 * Tooltip — manifest §Tooltip.
 *
 *   <Tooltip content="Helpful text"><Button>Save</Button></Tooltip>
 *
 * Children are wrapped in the Radix Trigger with asChild, so any single
 * element can carry the tooltip. `delayMs` overrides the provider's
 * delayDuration for this tooltip only.
 */

export interface TooltipProps {
  /** Tooltip body — rendered inside the inverse surface bubble. */
  content: React.ReactNode;
  /** Single element that triggers the tooltip on hover/focus. */
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** Per-tooltip open delay; falls back to the provider default (300ms). */
  delayMs?: number;
}

export function Tooltip({
  content,
  children,
  side = "top",
  delayMs,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root delayDuration={delayMs}>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          {/* Empty content → no bubble (MUI `title=""` semantics — hover gates the tip). */}
          {content ? (
            <TooltipPrimitive.Content
              side={side}
              sideOffset={6}
              className="z-(--z-popover) rounded-md bg-surface-inverse px-2 py-1 type-caption text-on-inverse"
            >
              {content}
            </TooltipPrimitive.Content>
          ) : null}
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
Tooltip.displayName = "Tooltip";

export interface TooltipProviderProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider> {}

/** App-level provider — skip delay and shared timeout scope for tooltips. */
export function TooltipProvider({
  delayDuration = 300,
  ...props
}: TooltipProviderProps) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />;
}
TooltipProvider.displayName = "TooltipProvider";
